import { useState } from 'react';
import { motion } from 'motion/react';
import { getLevelName, getReadingStage } from '../../../config/levelConfig.js';
import { todayStr } from '../../../lib/utils.js';

function startOfDay(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function formatDayLabel(dayTs) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yday = today.getTime() - 86400000;
  if (dayTs === today.getTime()) return 'Today';
  if (dayTs === yday) return 'Yesterday';
  return new Date(dayTs).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatLastSessionRelative(dayTs) {
  if (!dayTs) return 'No sessions yet';
  const today = startOfDay(Date.now());
  const diffDays = Math.max(0, Math.floor((today - dayTs) / 86400000));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
}

function familyStatus(dayTs) {
  if (!dayTs) return { label: 'No recent practice', dotClass: 'bg-finley-text-muted/45' };
  const today = startOfDay(Date.now());
  const diffDays = Math.max(0, Math.floor((today - dayTs) / 86400000));
  if (diffDays === 0) return { label: 'Practiced today', dotClass: 'bg-finley-success' };
  if (diffDays <= 2) return { label: 'Recently practiced', dotClass: 'bg-finley-warning' };
  return { label: 'Needs a session', dotClass: 'bg-finley-error' };
}

function buildSessionHistory(log = []) {
  const map = new Map();
  for (const row of log) {
    if (!row?.t) continue;
    const day = startOfDay(row.t);
    if (!map.has(day)) map.set(day, []);
    map.get(day).push(row);
  }
  return [...map.entries()]
    .map(([day, entries]) => {
      const ts = entries.map((e) => e.t).sort((a, b) => a - b);
      const spanMin = ts.length > 1 ? Math.round((ts[ts.length - 1] - ts[0]) / 60000) : 0;
      const minutes = Math.max(1, Math.min(Math.round(spanMin + entries.length * 0.25), entries.length * 3));
      const modes = [...new Set(entries.map((e) => e.m).filter(Boolean))];
      const correct = entries.filter((e) => e.c).length;
      const accuracy = entries.length ? Math.round((correct / entries.length) * 100) : 0;
      return { day, entries, minutes, modes, correct, attempted: entries.length, accuracy };
    })
    .sort((a, b) => b.day - a.day);
}

function calcStreak(history) {
  if (!history.length) return 0;
  const daySet = new Set(history.map((h) => h.day));
  let day = startOfDay(Date.now());
  let streak = 0;
  while (daySet.has(day)) {
    streak += 1;
    day -= 86400000;
  }
  return streak;
}

function summarizeSession(session) {
  if (!session) return 'No recent activity yet.';
  const modeLabels = {
    phonics: 'phonics',
    sight: 'sight words',
    stories: 'stories',
    bubbles: 'bubble pop'
  };
  const modes = session.modes.map((m) => modeLabels[m] || m);
  const modeText = modes.length ? `Practiced ${modes.slice(0, 2).join(' and ')}` : 'Completed a focused reading session';
  return `${modeText}. Accuracy was ${session.accuracy}%, and Finley tuned the next activities accordingly.`;
}

function getRecommendation(profile, history) {
  const now = Date.now();
  const lastSession = history[0];
  const level = profile?.tracker?.currentLevel || 1;
  const bkt = profile?.tracker?.bkt || {};
  const lowMastery = Object.entries(bkt).find(([, p]) => p >= 0.6 && p < 0.85);
  const weekHistory = history.filter((s) => s.day >= startOfDay(now - 6 * 86400000));
  const avgWeekMinutes = weekHistory.length ? Math.round(weekHistory.reduce((sum, s) => sum + s.minutes, 0) / weekHistory.length) : 0;
  const daysSinceLast = lastSession ? Math.floor((startOfDay(now) - lastSession.day) / 86400000) : 999;

  if (daysSinceLast >= 3) {
    return {
      text: "Your child hasn't practiced in a few days. Even a short session keeps the momentum going!",
      action: 'Start session',
      target: 'start'
    };
  }
  if (lowMastery) {
    const skill = lowMastery[0].replace(/_/g, ' ');
    return {
      text: `Your child is close to mastering ${skill}. A couple more sessions should get them there!`,
      action: 'Start session',
      target: 'start'
    };
  }
  if (weekHistory.length >= 3 && avgWeekMinutes <= 8) {
    return {
      text: 'Your child completed sessions quickly this week. Consider increasing session length in Settings to maximize learning time.',
      action: 'Open settings',
      target: 'settings'
    };
  }
  const nextLevelName = getLevelName(Math.min(level + 1, 15));
  const avgBkt = Object.values(bkt).length ? Object.values(bkt).reduce((s, v) => s + v, 0) / Object.values(bkt).length : 0;
  if (avgBkt >= 0.75 && level < 15) {
    return {
      text: `Your child is making strong progress as a ${getLevelName(level)}. ${nextLevelName} is just around the corner!`,
      action: null,
      target: null
    };
  }
  return {
    text: 'Consistency is key! Regular practice 3-4 times per week leads to the strongest reading gains.',
    action: 'Start session',
    target: 'start'
  };
}

export function HomeTab({ profile, profiles = [], activeId, onSelectChild, onStartSession, onExtraPractice, onOpenStories, onOpenSettings }) {
  const level = profile?.tracker?.currentLevel || 1;
  const stage = getReadingStage(level);
  const levelName = getLevelName(level);
  const history = buildSessionHistory(profile?.tracker?.log || []);
  const lastSession = history[0];
  const sessionsThisWeek = history.filter((s) => s.day >= startOfDay(Date.now() - 6 * 86400000)).length;
  const streak = calcStreak(history);
  const recommendation = getRecommendation(profile, history);
  const showFamilyOverview = profiles.length > 1;
  const [showCoachingCard, setShowCoachingCard] = useState(() => {
    try {
      return localStorage.getItem('rq:coachingCardDismissedDate') !== todayStr();
    } catch {
      return true;
    }
  });

  const dismissCoachingCard = () => {
    try {
      localStorage.setItem('rq:coachingCardDismissedDate', todayStr());
    } catch {}
    setShowCoachingCard(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-5">
      {showFamilyOverview && (
        <div className="mb-4 rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-5 shadow-finley-sm">
          <div className="mb-3 font-finley-display text-xl text-finley-text">All Readers</div>
          {profiles.map((child) => {
            const childLevel = child?.tracker?.currentLevel || 1;
            const childHistory = buildSessionHistory(child?.tracker?.log || []);
            const childLastDay = childHistory[0]?.day || null;
            const status = familyStatus(childLastDay);
            const isActive = child.id === activeId;
            return (
              <button
                key={child.id}
                onClick={() => onSelectChild && onSelectChild(child.id)}
                className={`mb-2 flex min-h-[52px] w-full items-center justify-between rounded-finley-md border px-3 py-2 text-left last:mb-0 ${
                  isActive ? 'border-finley-primary/35 bg-finley-primary/5' : 'border-finley-text-muted/15 bg-finley-bg'
                }`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-finley-full bg-finley-primary/10 text-sm">
                    {child.avatar || child?.name?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-finley-text">{child.name}</div>
                    <div className="truncate text-xs text-finley-text-light">{getLevelName(childLevel)}</div>
                  </div>
                </div>
                <div className="ml-3 text-right">
                  <div className="text-xs font-semibold text-finley-text-light">{formatLastSessionRelative(childLastDay)}</div>
                  <div className="mt-1 flex items-center justify-end gap-1">
                    <span className={`inline-block h-2 w-2 rounded-finley-full ${status.dotClass}`} />
                    <span className="text-[11px] text-finley-text-light">{status.label}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="mb-4 rounded-finley-xl border-2 border-finley-primary/20 bg-finley-primary/5 p-5 shadow-finley-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-finley-display text-3xl text-finley-text">{levelName}</div>
            <div className="mt-1 text-sm text-finley-text-light">{stage?.stageName || 'Reading Stage'}</div>
          </div>
          {history.length > 0 ? (
            <div className="space-y-2 text-right">
              <div className="rounded-finley-full bg-finley-bg-card px-3 py-1 text-xs font-semibold text-finley-primary shadow-finley-sm">{sessionsThisWeek} sessions this week</div>
              <div className="rounded-finley-full bg-finley-bg-card px-3 py-1 text-xs font-semibold text-finley-secondary shadow-finley-sm">{streak} day streak</div>
            </div>
          ) : (
            <div className="rounded-finley-full bg-finley-bg-card px-3 py-1 text-xs font-semibold text-finley-text-light shadow-finley-sm">Ready to start!</div>
          )}
        </div>
      </div>

      <div className="mb-4 rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-4 shadow-finley-sm">
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-finley-text-light">Quick Access</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onExtraPractice || onStartSession}
            className="min-h-[40px] rounded-finley-full border bg-transparent px-4 py-2 text-[13px] font-semibold shadow-finley-sm"
            style={{ borderColor: '#0D9488', color: '#0D9488' }}
          >
            Extra Practice
          </button>
          <div className="rounded-finley-lg border border-finley-primary/20 bg-finley-primary/5 p-2">
            <button
              onClick={onOpenStories || onStartSession}
              className="min-h-[40px] w-full rounded-finley-full border border-finley-primary bg-transparent px-4 py-2 text-[13px] font-semibold text-finley-primary shadow-finley-sm"
            >
              Bedtime Stories
            </button>
            <div className="mt-1 px-1 text-[11px] leading-4 text-finley-text-light">
              Calm read-aloud stories for wind-down time.
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4 rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-5 shadow-finley-sm">
        <div className="mb-2 flex items-center justify-between">
          <div className="font-finley-display text-xl text-finley-text">Last Session</div>
          <div className="text-xs font-semibold text-finley-text-light">{lastSession ? formatDayLabel(lastSession.day) : 'No sessions yet'}</div>
        </div>
        {lastSession ? (
          <>
            <div className="mb-2 text-sm text-finley-text-light">{lastSession.modes.length} activities • {lastSession.minutes} min</div>
            <p className="m-0 text-sm leading-6 text-finley-text-light">{summarizeSession(lastSession)}</p>
          </>
        ) : (
          <>
            <p className="mb-3 text-sm text-finley-text-light">No sessions yet. Start your child's first adventure!</p>
            <button onClick={onStartSession} className="min-h-[44px] rounded-finley-lg bg-finley-primary px-4 py-2 text-sm font-semibold text-white shadow-finley-sm">Start Session</button>
          </>
        )}
      </div>

      <div className="mb-4 rounded-finley-xl border-2 border-finley-accent/20 bg-finley-accent/5 p-5 shadow-finley-sm">
        <div className="mb-2 flex items-center gap-2 font-finley-display text-xl text-finley-text">
          <span>✨</span>
          <span>Finley Recommends</span>
        </div>
        <p className="m-0 text-sm leading-6 text-finley-text-light">{recommendation.text}</p>
        {recommendation.action && (
          <button
            onClick={recommendation.target === 'settings' ? onOpenSettings : onStartSession}
            className="mt-2 text-sm font-semibold text-finley-primary underline decoration-transparent underline-offset-2 transition hover:decoration-finley-primary"
          >
            {recommendation.action}
          </button>
        )}
      </div>

      {showCoachingCard && (
        <div
          className="relative mb-4 rounded-finley-lg p-5 shadow-finley-sm"
          style={{
            background: 'linear-gradient(135deg, rgba(13,148,136,0.06), rgba(13,148,136,0.02))',
            border: '1px solid rgba(13,148,136,0.18)'
          }}
        >
          <button
            onClick={dismissCoachingCard}
            aria-label="Dismiss today's suggestion"
            className="absolute right-3 top-3 rounded-finley-full px-2 py-0.5 text-sm"
            style={{ color: 'rgba(15,43,60,0.58)' }}
          >
            ×
          </button>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#0D9488' }}>Try this today</div>
          <div className="mb-2 flex items-center gap-2" style={{ color: '#0F2B3C' }}>
            <span className="text-lg">📖</span>
            <h3 className="m-0 font-finley-display text-lg">Join one session this week</h3>
          </div>
          <p className="mb-4 text-[13px] leading-[1.55]" style={{ color: 'rgba(15,43,60,0.58)' }}>
            Science shows that a parent asking even one discussion question after a reading session acts like a personal tutor.
            You don&apos;t need to teach - just ask: &quot;What was your favorite part?&quot;
          </p>
          <button
            onClick={onStartSession}
            className="min-h-[44px] rounded-finley-lg border-2 bg-transparent px-4 py-2 text-sm font-semibold"
            style={{ borderColor: '#0D9488', color: '#0D9488' }}
          >
            Start a session together
          </button>
        </div>
      )}

      <div className="rounded-finley-xl border-2 border-finley-text-muted/15 bg-finley-bg-card p-5 shadow-finley-sm">
        <div className="mb-3 font-finley-display text-xl text-finley-text">Activity Feed</div>
        {history.slice(0, 10).map((session) => (
          <div key={session.day} className="mb-3 border-b border-finley-text-muted/10 pb-3 last:mb-0 last:border-b-0 last:pb-0">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="font-semibold text-finley-text">{formatDayLabel(session.day)}</span>
              <span className="text-finley-text-light">{session.minutes} min</span>
            </div>
            <div className="mb-1 text-xs text-finley-text-light">{session.modes.length} activities completed</div>
            <div className="text-sm text-finley-text-light">{summarizeSession(session)}</div>
          </div>
        ))}
        {history.length === 0 && <p className="m-0 text-sm text-finley-text-light">No activity yet. Start a short session to unlock insights.</p>}
        {history.length > 0 && history.length < 3 && <p className="mt-2 text-sm text-finley-text-light">Keep going! The more sessions your child completes, the more insights Finley can provide.</p>}
        {history.length > 10 && <button className="mt-2 text-sm font-semibold text-finley-primary underline decoration-transparent underline-offset-2 transition hover:decoration-finley-primary">View all</button>}
      </div>
    </motion.div>
  );
}
